import React, { createContext, useContext } from 'react';
import ApiClient from '../../lib/api-client';
import { IAssignmentCreationDTO } from '@common-frontend-submodule/interfaces/assignment/iassignment-creation.interface';
import { IAssignmentUpdateDTO } from '@common-frontend-submodule/interfaces/assignment/iassignment-update.interface';
import { IGetAssignmentsRequestDTO } from '@common-frontend-submodule/interfaces/assignment/iget-assignments-request.interface';
import { IAssignmentDTO } from '@common-frontend-submodule/interfaces/assignment/iassignment.interface';
import { IPaginatedResultDTO } from '@common-frontend-submodule/interfaces/common/ipaginated-result.interface';
import { IApiResponseGenericDTO } from '@common-frontend-submodule/interfaces/common/iapi-response.interface';
import { AssignmentRoutes } from '@common-frontend-submodule/enums/routes/assignment-routes.enum';

const apiClient = ApiClient.getInstance();

interface AssignmentContextProps {
  getById: (id: string) => Promise<IApiResponseGenericDTO<IAssignmentDTO>>;
  allAssignments: (id: string) => Promise<IApiResponseGenericDTO<IAssignmentDTO[]>>;
  filter: (model: IGetAssignmentsRequestDTO) => Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IAssignmentDTO>>>;
  create: (model: IAssignmentCreationDTO) => Promise<IApiResponseGenericDTO<IAssignmentDTO>>;
  update: (id: string, model: IAssignmentUpdateDTO) => Promise<IApiResponseGenericDTO<IAssignmentDTO>>;
  delete: (id: string) => Promise<IApiResponseGenericDTO<boolean>>;
  allClientAssignments: (clientId: string) => Promise<IApiResponseGenericDTO<IAssignmentDTO[]>>;
}

const AssignmentContext = createContext<AssignmentContextProps | undefined>(undefined);

export const AssignmentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const getById = async (accountId: string) => {
    return await apiClient.getRequest<IAssignmentDTO>(`${AssignmentRoutes.BasePrefix}/${accountId}`);
  };

  const allAssignments = async (id: string) => {
    return await apiClient.getRequest<IAssignmentDTO[]>(`${AssignmentRoutes.BasePrefix}/all/${id}`);
  };

  const filter = async (model: IGetAssignmentsRequestDTO) => {
    return await apiClient.postRequest<IGetAssignmentsRequestDTO, IPaginatedResultDTO<IAssignmentDTO>>(`${AssignmentRoutes.BasePrefix}/${AssignmentRoutes.List}`, model);
  };

  const create = async (model: IAssignmentCreationDTO) => {
    return await apiClient.postRequest<IAssignmentCreationDTO, IAssignmentDTO>(AssignmentRoutes.BasePrefix, model);
  };

  const update = async (id: string, model: IAssignmentUpdateDTO) => {
    return await apiClient.putRequest<IAssignmentUpdateDTO, IAssignmentDTO>(`${AssignmentRoutes.BasePrefix}/${id}`, model);
  };

  const deleteAssignment = async (id: string) => {
    return await apiClient.deleteRequest<null, boolean>(`${AssignmentRoutes.BasePrefix}/${id}`);
  };

  const allClientAssignments = async (clientId: string) => {
    return await apiClient.getRequest<IAssignmentDTO[]>(`${AssignmentRoutes.BasePrefix}/all-client-assignment/${clientId}`);
  }


  return (
    <AssignmentContext.Provider value={{ allClientAssignments, getById, allAssignments: allAssignments, filter, create, update, delete: deleteAssignment }}>
      {children}
    </AssignmentContext.Provider>
  );
};

export const useAssignmentStore = () => {
  const context = useContext(AssignmentContext);
  if (!context) {
    throw new Error('useAssignmentStore must be used within an AssignmentProvider');
  }
  return context;
};
